import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Phone,
  Mail,
  ChevronUp,
  ChevronDown,
  Trash2,
  History,
  Clock
} from 'lucide-react';

const CompanyUsersTable = ({ users, onSort, sortConfig, onDelete, onOpenLoginActivity, onOpenTimeline, isUserManager, currentUserId, onToggleUserManager }) => {
  const { t } = useTranslation();
  const SortButton = ({ column, children }) => (
    <button
      onClick={() => onSort(column)}
      className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors"
    >
      {children}
      {sortConfig.key === column ? (
        sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
      ) : (
        <ChevronUp size={16} className="opacity-30" />
      )}
    </button>
  );

  const StatusBadge = ({ isActive, isStaff }) => {
    if (!isActive) {
      return (
        <span className="px-2 py-0.5 rounded-full border bg-red-100 text-red-800 border-red-200">
          {t('companyAdmins.inactive')}
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full border bg-green-100 text-green-800 border-green-200">
        {t('companyAdmins.active')}
      </span>
    );
  };


  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: '2-digit',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full bg-white">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="text-left py-3 px-4 ">
              <SortButton column="user.firstName">{t('companyAdmins.user')}</SortButton>
            </th>
            <th className="text-left py-3 px-4 ">
              <SortButton column="user.email">{t('companyAdmins.email')}</SortButton>
            </th>
            <th className="text-left py-3 px-4 ">
              <SortButton column="user.email">{t('companyAdmins.contactNo')}</SortButton>
            </th>
            {/* <th className="text-left py-3 px-4 ">
              <SortButton column="role">Role</SortButton>
            </th> */}
            <th className="text-left py-3 px-4 ">
              <SortButton column="user.is_active">{t('companyAdmins.status')}</SortButton>
            </th>
            <th className="text-left py-3 px-4 ">
              <SortButton column="is_usermanager">{t('companyAdmins.userManager')}</SortButton>
            </th>
            <th className="text-left py-3 px-4 ">
              <SortButton column="user.date_joined">{t('companyAdmins.joinedLabel')}</SortButton>
            </th>
            {isUserManager && (
              <th className="text-left py-3 px-4 ">
                <span className="text-gray-700 font-semibold">{t('companyAdmins.actions', 'Actions')}</span>
              </th>
            )}
            {/* <th className="text-left py-3 px-4 ">
              <SortButton column="user.last_login">Last Login</SortButton>
            </th> */}
          </tr>
        </thead>
        <tbody>
          {users.map((userItem, index) => (
            <tr
              key={userItem.id}
              className={`border-b border-gray-100 hover:bg-blue-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}
            >
              {/* User Info */}
              <td className="py-2 px-4 ">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white">
                    {userItem.user.firstName?.[0]}{userItem.user.lastName?.[0]}
                  </div>
                  <div>
                    <div className="text-gray-900">
                      {userItem.user.firstName} {userItem.user.lastName}
                    </div>
                  </div>
                </div>
              </td>

              {/* Contact Info */}
              <td className="py-2 px-4 ">
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-gray-700">
                    <Mail size={12} className="text-gray-400" />
                    <span className="">{userItem.user.email}</span>
                  </div>
                </div>
              </td>
              <td className="py-2 px-4 ">
                <div className="space-y-1">
                  {userItem.user.profile?.mobile && (
                    <div className="flex items-center gap-1 text-gray-600">
                      <Phone size={12} className="text-gray-400" />
                      <span className="">
                        {userItem.user.profile.country_code} {userItem.user.profile.mobile}
                      </span>
                    </div>
                  )}
                </div>
              </td>
              {/* Role */}


              {/* Status */}
              <td className="py-2 px-4 ">
                <StatusBadge
                  isActive={userItem.user.is_active}
                  isStaff={userItem.user.is_staff}
                />
              </td>

              {/* User Manager */}
              <td className="py-2 px-4 ">
                <div className="flex items-center">
                  {isUserManager && userItem.user.id !== currentUserId ? (
                    <button
                      onClick={() => onToggleUserManager(userItem.user.id, !userItem.is_usermanager)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${userItem.is_usermanager ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      role="switch"
                      aria-checked={userItem.is_usermanager}
                    >
                      <span className="sr-only">{t('companyAdmins.toggleUserManager')}</span>
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${userItem.is_usermanager ? 'translate-x-5' : 'translate-x-0'
                          }`}
                      />
                    </button>
                  ) : (
                    userItem.is_usermanager ? (
                      <span className="px-2 py-0.5 rounded-full border bg-blue-100 text-blue-800 border-blue-200">
                        {t('companyAdmins.yes')}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full border bg-gray-100 text-gray-600 border-gray-200">
                        {t('companyAdmins.no')}
                      </span>
                    )
                  )}
                </div>
              </td>

              {/* Joined Date */}
              <td className="py-2 px-4 ">
                <div className="flex items-center gap-1 text-gray-700">
                  <Calendar size={12} className="text-gray-400" />
                  <span>{formatDate(userItem.user.date_joined)}</span>
                </div>
              </td>

              {/* Actions */}
              {isUserManager && (
                <td className="py-2 px-4 ">
                  <div className="flex items-center gap-1">
                    {userItem.user.id !== currentUserId && (
                      <>
                        {onOpenTimeline && (
                          <button
                            type="button"
                            onClick={() => onOpenTimeline(userItem.user.id, `${userItem.user.firstName || ''} ${userItem.user.lastName || ''}`.trim())}
                            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title={t('companyAdmins.viewTimeline', 'View timeline')}
                          >
                            <Clock size={18} />
                          </button>
                        )}
                        {onOpenLoginActivity && (
                          <button
                            type="button"
                            onClick={() => onOpenLoginActivity(userItem.user.id, `${userItem.user.firstName || ''} ${userItem.user.lastName || ''}`.trim())}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title={t('companyAdmins.viewLoginActivity', 'View login activity')}
                          >
                            <History size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => onDelete(userItem.user.id, `${userItem.user.firstName} ${userItem.user.lastName}`)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title={t('companyAdmins.delete', 'Delete')}
                        >
                          <Trash2 size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              )}

            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CompanyUsersTable;